import React, { useState } from "react";
import { DeviceSummary } from "../types";
import { useDevice, useDeviceBundles, useKillDevice } from "../hooks/useDevices";

interface DeviceDetailModalProps {
  device: DeviceSummary | null;
  onClose: () => void;
}

export const DeviceDetailModal: React.FC<DeviceDetailModalProps> = ({
  device,
  onClose,
}) => {
  const [killReason, setKillReason] = useState("");
  const [showKillConfirm, setShowKillConfirm] = useState(false);

  const deviceId = device?.id || "";
  const { data: detail, isLoading: loadingDetail } = useDevice(deviceId);
  const { data: bundles, isLoading: loadingBundles } = useDeviceBundles(deviceId);

  const killMutation = useKillDevice();

  if (!device) return null;

  const handleKill = (e: React.FormEvent) => {
    e.preventDefault();
    if (!killReason.trim()) return;
    killMutation.mutate(
      { deviceId: device.id, reason: killReason },
      {
        onSuccess: () => {
          setShowKillConfirm(false);
          onClose();
        },
      }
    );
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-container modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2>{device.name}</h2>
            <span className={`status-pill status-${device.status}`}>
              {device.status}
            </span>
            {device.kill_switched && (
              <span className="status-pill status-error margin-left">
                KILL-SWITCHED
              </span>
            )}
          </div>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {loadingDetail ? (
            <div className="loading-spinner">Loading device metadata...</div>
          ) : (
            <div className="detail-section-grid">
              <div className="info-card">
                <h3>Network Configuration</h3>
                <div className="info-row">
                  <span>Mesh IP:</span>
                  <code>{device.mesh_ip}</code>
                </div>
                <div className="info-row">
                  <span>Endpoint:</span>
                  <span>{device.endpoint || "Not discovered yet"}</span>
                </div>
                <div className="info-row">
                  <span>NAT Type:</span>
                  <span>{device.nat_type || "Unknown / Symmetric"}</span>
                </div>
                <div className="info-row">
                  <span>MTU:</span>
                  <span>{device.mtu} bytes</span>
                </div>
                <div className="info-row">
                  <span>Config Version:</span>
                  <span>v{device.config_version}</span>
                </div>
              </div>

              <div className="info-card">
                <h3>Hardware & System</h3>
                <div className="info-row">
                  <span>RouterOS Version:</span>
                  <span>{device.ros_version || "N/A"}</span>
                </div>
                <div className="info-row">
                  <span>Board/Model:</span>
                  <span>{device.board || "Generic MikroTik"}</span>
                </div>
                <div className="info-row">
                  <span>Mesh Role:</span>
                  <span>{device.role || "standard"}</span>
                </div>
                <div className="info-row">
                  <span>Last Seen:</span>
                  <span>{new Date(device.last_seen).toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="bundles-section">
            <h3>Configuration Bundle History</h3>
            {loadingBundles ? (
              <div>Loading bundles...</div>
            ) : !bundles || bundles.length === 0 ? (
              <div className="empty-text">No signed bundles recorded yet.</div>
            ) : (
              <div className="bundles-list">
                {bundles.map((b) => (
                  <div key={b.version} className="bundle-card">
                    <div className="bundle-header">
                      <span>Version {b.version}</span>
                      <small>{new Date(b.created_at).toLocaleString()}</small>
                    </div>
                    {b.sig && (
                      <div className="bundle-sig">
                        <strong>HMAC Signature:</strong> <code>{b.sig.slice(0, 24)}...</code>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {!device.kill_switched && (
            <div className="kill-section">
              {!showKillConfirm ? (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowKillConfirm(true)}
                >
                  🚫 Emergency Kill Switch
                </button>
              ) : (
                <form onSubmit={handleKill} className="kill-form">
                  <p className="warning-text">
                    ⚠️ <strong>Warning:</strong> Kill-switching this router revokes its WireGuard configuration immediately.
                  </p>
                  <input
                    type="text"
                    placeholder="Enter reason for kill switch..."
                    value={killReason}
                    onChange={(e) => setKillReason(e.target.value)}
                    required
                  />
                  <div className="modal-actions inline">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowKillConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-danger"
                      disabled={killMutation.isPending}
                    >
                      {killMutation.isPending ? "Killing..." : "Confirm Kill"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
